// Studio Row in the zone framework (docs/build/zones.md): main.ts imports this module for its side effects. It
// registers the zone's own sections (zones/creators/zone.json "sections"):
//   spotlights[:cr_a,cr_b]  two or three creators, wide banners, Follow. From 23 Oct to 11 Nov the haunt makers lead
//                           (the creators of Halloween-row games, when there are at least three). Ids in the arg are
//                           the showrunner's pinned picks; the rest rotate weekly.
//   directory               every creator, grouped by what they make, with a search of its own.
// It also supplies Home's "creators you follow" row (`creatorsYouFollow`), which banger-zone-home places.
//
// Section kinds are global, so each handler draws only for this zone and hands any other zone to whatever was
// registered before it.

import type { Catalogue } from "../../catalogue.ts";
import { h } from "../../dom.ts";
import { registerSection, sectionRenderer, type SectionContext, type SectionRender } from "../registry.ts";
import { GROUP_LABEL, HOSTS_LABEL, hauntMakers, searchStudios, spotlightPicks, studios, type Group, type Studio } from "./data.ts";
import { followedCreators } from "./follow.ts";
import { ensureCreatorsStyle } from "./style.ts";
import { spotlightCard, studioCard } from "./ui.ts";

export const ZONE_ID = "creators";

function own(kind: string, render: (ctx: SectionContext) => HTMLElement | null | Promise<HTMLElement | null>): void {
  const before: SectionRender | undefined = sectionRenderer(kind);
  registerSection(kind, (ctx) => {
    if (ctx.zone.id !== ZONE_ID) return before ? before(ctx) : null;
    ensureCreatorsStyle();
    return render(ctx);
  });
}

own("spotlights", async (ctx) => {
  const cat = await ctx.catalogue();
  const haunt = hauntMakers(cat, ctx.now);
  const pinned = (ctx.arg ?? "").split(",").filter((x) => x.startsWith("cr_"));
  const picks = haunt.length ? haunt.slice(0, 3) : spotlightPicks(studios(cat), ctx.now, pinned);
  if (picks.length < 2) return null;
  return spotlights(picks, haunt.length ? { eyebrow: "Open Haunt", title: "Haunt makers", lead: "The people behind this week's spooky games." } : { eyebrow: "This week", title: "In the spotlight" });
});

export function spotlights(picks: readonly Studio[], head: { eyebrow: string; title: string; lead?: string }): HTMLElement {
  return h(
    "section",
    { class: "cr-spotlights", "aria-labelledby": "cr-spot-title" },
    h("p", { class: "cr-eyebrow" }, head.eyebrow),
    h("h2", { class: "zone-h2", id: "cr-spot-title" }, head.title),
    head.lead ? h("p", { class: "cr-lead" }, head.lead) : null,
    h("ul", { class: "cr-spot-list", role: "list" }, picks.map((s) => spotlightCard(s))),
  );
}

own("directory", async (ctx) => {
  const all = studios(await ctx.catalogue());
  return all.length ? directory(all) : null;
});

/** a group's first page: two rows of six on desktop, three rows of two on a phone */
const groupShow = () => (typeof matchMedia === "function" && matchMedia("(max-width: 640px)").matches ? 6 : 12);
const SEARCH_SHOW = 48;

/** People before hosts, then best-lit windows first: more games with real art, then by name. */
function ranked(list: readonly Studio[]): Studio[] {
  return [...list].sort((a, b) => Number(a.who === "host") - Number(b.who === "host") || b.realArt - a.realArt || b.games.length - a.games.length || a.creator.name.localeCompare(b.creator.name, "en", { sensitivity: "base" }));
}

export function directory(all: readonly Studio[]): HTMLElement {
  // named people and studios first, by what they make; hosts and portals last, in one quiet group
  const people = all.filter((s) => s.who === "person");
  const groups: { g: Group | "web"; label: string; list: Studio[] }[] = [
    ...(["games", "worlds", "learn"] as Group[]).map((g) => ({ g, label: GROUP_LABEL[g], list: ranked(people.filter((s) => s.group === g)) })),
    { g: "web" as const, label: HOSTS_LABEL, list: ranked(all.filter((s) => s.who === "host")) },
  ].filter((x) => x.list.length);
  const body = h("div", { class: "cr-dir-body" });
  const status = h("p", { class: "cr-dir-count", role: "status", "aria-live": "polite" });
  const input = h("input", { type: "search", class: "cr-dir-search", placeholder: "Find a creator or one of their games", "aria-label": "Find a creator", autocomplete: "off", enterkeyhint: "search" });

  const grouped = () => {
    const show = groupShow();
    return groups.map(({ g, label, list }) => {
      const ul = h("ul", { class: "cr-grid", role: "list" }, list.slice(0, show).map(studioCard));
      const more =
        list.length > show
          ? h(
              "button",
              {
                type: "button",
                class: "btn ghost small cr-more",
                onclick: (e: Event) => {
                  ul.append(...list.slice(show).map(studioCard));
                  (e.currentTarget as HTMLElement).remove();
                },
              },
              `All ${list.length}`,
            )
          : null;
      return h(
        "section",
        { class: `cr-group${g === "web" ? " cr-group-web" : ""}`, "data-group": g },
        h("h3", { class: "cr-group-title" }, label, h("span", { class: "cr-group-n" }, String(list.length))),
        g === "web" ? h("p", { class: "cr-group-note" }, "Games we found on the web, listed by the site they're on until their makers claim them.") : null,
        ul,
        more,
      );
    });
  };

  const paint = () => {
    const q = input.value;
    if (!q.trim()) {
      status.textContent = `${all.length} creators`;
      body.replaceChildren(...grouped());
      return;
    }
    const hits = ranked(searchStudios(all, q));
    status.textContent = hits.length ? `${hits.length} ${hits.length === 1 ? "creator" : "creators"}` : "Nobody by that name yet.";
    body.replaceChildren(h("ul", { class: "cr-grid", role: "list" }, hits.slice(0, SEARCH_SHOW).map(studioCard)));
  };
  let t = 0;
  input.addEventListener("input", () => {
    window.clearTimeout(t);
    t = window.setTimeout(paint, 120);
  });
  paint();
  return h(
    "section",
    { class: "cr-directory", "aria-labelledby": "cr-dir-title" },
    h("div", { class: "cr-dir-head" }, h("div", null, h("p", { class: "cr-eyebrow" }, "The directory"), h("h2", { class: "zone-h2", id: "cr-dir-title" }, "Every studio on the row")), h("form", { class: "cr-dir-form", role: "search", onsubmit: (e: Event) => e.preventDefault() }, input, status)),
    body,
  );
}

/**
 * Home's "creators you follow" row, for banger-zone-home to place: the studios the signed-in player follows that
 * have a listed game, newest follow order kept. null (nothing drawn) signed out or following nobody.
 */
export async function creatorsYouFollow(cat: Pick<Catalogue, "games" | "creators">): Promise<HTMLElement | null> {
  const ids = await followedCreators();
  if (!ids.length) return null;
  const by = new Map(studios(cat).map((s) => [s.creator.id, s] as const));
  const list = ids.map((id) => by.get(id)).filter((s): s is Studio => !!s);
  if (!list.length) return null;
  ensureCreatorsStyle();
  return h(
    "section",
    { class: "cr-following shelf", "aria-labelledby": "cr-following-title" },
    h("h2", { id: "cr-following-title" }, "Creators you follow"),
    h("ul", { class: "cr-row", role: "list" }, list.slice(0, 20).map(studioCard)),
  );
}
